/* eslint-disable require-jsdoc */
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Box,
  TablePagination,
} from '@mui/material/';
import {
  StyledTableCell,
  StyledInput,
  ExportButton,
  NextButton,
  TicketButton,
} from 'components/Common/MUIStyledComponents';
import { exportToCSV } from 'components/Common/CSVManage';
import { useSnackbar } from 'notistack';
import Modal from 'react-modal';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';

interface ITableProps {
  hits: any;
}

interface Data {
  field: string;
  column: string;
  row: number;
  dataSet: string;
  hits: number;
}

// CUSTOM TABLE HEADER
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc' ?
    (a, b) => descendingComparator(a, b, orderBy) :
    (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [ el, index ] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  id: keyof Data;
  label: string;
}

const headCells: HeadCell[] = [
  { id: 'field', label: 'Field Input' },
  { id: 'column', label: 'Column Name' },
  { id: 'row', label: 'Row #' },
  { id: 'dataSet', label: 'Data Set' },
  { id: 'hits', label: 'Documents' },
];

interface EnhancedTableProps {
  classes: any;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => void;
  order: Order;
  orderBy: string;
}

const EnhancedTableHead = (props: EnhancedTableProps) => {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell padding='checkbox'></StyledTableCell>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align='left'
            padding='normal'
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

// CUSTOM TABLE ROWS

const Row = styled(TableRow)({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
});

const CustomRow = (props: { row: any; rowNum: string; bigPage: number }) => {
  const { row, bigPage, rowNum } = props;
  const [ open, setOpen ] = useState('');
  const [ page, setPage ] = useState(1);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const convertObject = (obj: any): any => {
    Object.keys(obj).forEach((key) => !obj[key] && delete obj[key]);

    return Object.keys(obj).map((key) => (
      <div key={key} className='doc-display-bulk'>
        <p>{key}</p>
        <p>{typeof obj[key] === 'string' ? obj[key] : typeof obj[key]}</p>
      </div>
    ));
  };

  useEffect(() => {
    setOpen('');
    setPage(1);
  }, [ bigPage ]);

  const handleOpen = () => {
    if (open === rowNum) {
      setOpen('');
      setPage(1);
    } else {
      setOpen(rowNum);
      setPage(1);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <StyledTableCell style={{ width: '100px' }}>
          <IconButton
            style={{ color: 'var(--alternative-text-color)' }}
            aria-label='expand row'
            size='small'
            onClick={handleOpen}
          >
            {open === rowNum ? <KeyboardArrowRight /> : <KeyboardArrowDown />}
          </IconButton>
        </StyledTableCell>
        <StyledTableCell component='th' scope='row'>
          {row.field}
        </StyledTableCell>
        <StyledTableCell>{row.column}</StyledTableCell>
        <StyledTableCell>{row.row}</StyledTableCell>
        <StyledTableCell>{row.dataSet}</StyledTableCell>
        <StyledTableCell>
          {row.hits === 100 ? '100+' : row.hits}
        </StyledTableCell>
      </Row>
      <TableRow>
        <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0, background: 'var(--border-color)', height: '0px' }} colSpan={6}>
          <Collapse in={open === rowNum} timeout='auto' unmountOnExit>
            <Box margin={1}>
              <Typography
                variant='h6'
                className=''
                gutterBottom
                component='div'
                style={{
                  color: 'var(--alternative-text-color)',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '120%',
                  marginBottom: '25px',
                }}
              >
                Documents:
              </Typography>
              <Box>
                {row.docs.length >= page - 1 ?
                  convertObject(row.docs[page - 1]) :
                  convertObject(row.docs[0])}
              </Box>
              <Pagination
                count={row.docs.length}
                page={page}
                onChange={handleChange}
              />
            </Box>
          </Collapse>
        </StyledTableCell>
      </TableRow>
    </React.Fragment>
  );
};

const PREFIX = 'ResultsTable';
const classes = {
  root: `${PREFIX}-root`,
  paper: `${PREFIX}-paper`,
  visuallyHidden: `${PREFIX}-visually-hidden`,
  table: `${PREFIX}-table`,
};

const Root = styled('div')(() => ({
  [`&.${classes.root}`]: {
    width: '100%',
  },
  [`& .${classes.paper}`]: {
    width: '100%',
    marginBottom: '10px',
    background: 'var(--bg-secondary)',
  },
  [`& .${classes.visuallyHidden}`]: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  [`&.${classes.table}`]: {
    minWidth: 750,
  },
}));

const ResultsTable: React.FC<ITableProps> = (props: ITableProps) => {
  const { hits } = props;

  const [ order, setOrder ] = useState<Order>('asc');
  const [ orderBy, setOrderBy ] = useState<keyof Data>('field');
  const [ page, setPage ] = useState(0);
  const [ rowsPerPage, setRowsPerPage ] = useState(5);
  const [ fileName, setFileName ] = useState('');
  const [ fileNameModalOpen, setFileNameModalOpen ] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, hits.length - page * rowsPerPage);

  const headers = [ 'Field Input', 'Column Name', 'Row #', 'Data Set', 'Hits' ];

  const handleCSV = () => {
    if (hits.length >= 1) {
      exportToCSV(fileName, [ ...hits ], headers, 'bulk');
    } else {
      enqueueSnackbar('No Result Data to Export', { variant: 'error' });
    }
  };

  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };

  const handleFileNameClose = () => {
    setFileNameModalOpen(false);
    setFileName('');
  };

  return (
    <>
      <div className='bulk-table-title'>
        <p>Search Results</p>
        <ExportButton
          onClick={() => {
            setFileNameModalOpen(true);
          }}
          disabled={!Object.keys(hits).length}
        >
          Export Data
          <div className='finish-page-svg-spacer'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M15 6.705C14.9922 6.6361 14.9771 6.56822 14.955 6.5025V6.435C14.9189 6.35788 14.8708 6.287 14.8125 6.225L10.3125 1.725C10.2505 1.66666 10.1796 1.61856 10.1025 1.5825H10.0275C9.95457 1.5435 9.8762 1.51569 9.795 1.5H5.25C4.65326 1.5 4.08097 1.73705 3.65901 2.15901C3.23705 2.58097 3 3.15326 3 3.75V14.25C3 14.8467 3.23705 15.419 3.65901 15.841C4.08097 16.2629 4.65326 16.5 5.25 16.5H12.75C13.3467 16.5 13.919 16.2629 14.341 15.841C14.7629 15.419 15 14.8467 15 14.25V6.75C15 6.75 15 6.75 15 6.705ZM10.5 4.0575L12.4425 6H11.25C11.0511 6 10.8603 5.92098 10.7197 5.78033C10.579 5.63968 10.5 5.44891 10.5 5.25V4.0575ZM13.5 14.25C13.5 14.4489 13.421 14.6397 13.2803 14.7803C13.1397 14.921 12.9489 15 12.75 15H5.25C5.05109 15 4.86032 14.921 4.71967 14.7803C4.57902 14.6397 4.5 14.4489 4.5 14.25V3.75C4.5 3.55109 4.57902 3.36032 4.71967 3.21967C4.86032 3.07902 5.05109 3 5.25 3H9V5.25C9 5.84674 9.23705 6.41903 9.65901 6.84099C10.081 7.26295 10.6533 7.5 11.25 7.5H13.5V14.25ZM9.9675 10.7175L9.75 10.9425V9C9.75 8.80109 9.67098 8.61032 9.53033 8.46967C9.38968 8.32902 9.19891 8.25 9 8.25C8.80109 8.25 8.61032 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V10.9425L8.0325 10.7175C7.89127 10.5763 7.69973 10.4969 7.5 10.4969C7.30027 10.4969 7.10873 10.5763 6.9675 10.7175C6.82627 10.8587 6.74693 11.0503 6.74693 11.25C6.74693 11.4497 6.82627 11.6413 6.9675 11.7825L8.4675 13.2825C8.53883 13.3508 8.62294 13.4043 8.715 13.44C8.80478 13.4797 8.90185 13.5002 9 13.5002C9.09815 13.5002 9.19522 13.4797 9.285 13.44C9.37706 13.4043 9.46117 13.3508 9.5325 13.2825L11.0325 11.7825C11.1737 11.6413 11.2531 11.4497 11.2531 11.25C11.2531 11.0503 11.1737 10.8587 11.0325 10.7175C10.8913 10.5763 10.6997 10.4969 10.5 10.4969C10.3003 10.4969 10.1087 10.5763 9.9675 10.7175Z'
                fill='var(--alternative-text-color)'
              />
            </svg>
          </div>
        </ExportButton>
      </div>
      <Root className={classes.root}>
        <Paper className={classes.paper}>
          <TableContainer>
            {Object.keys(hits).length ? (
              <Table
                className={classes.table}
                aria-labelledby='tableTitle'
                aria-label='enhanced table'
              >
                <EnhancedTableHead
                  classes={classes}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />

                <TableBody>
                  {stableSort(hits, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <CustomRow
                          key={index}
                          row={row}
                          rowNum={`${page}${index}`}
                          bigPage={page}
                        />
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow>
                      <StyledTableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : (
              <>
                <Table
                  className={classes.table}
                  aria-labelledby='tableTitle'
                  aria-label='enhanced table'
                >
                  <EnhancedTableHead
                    classes={classes}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                  />
                </Table>
                <div className='table-placeholder'>
                  Submit data to see results
                </div>
              </>
            )}
          </TableContainer>
          {Object.keys(hits).length ? (
            <TablePagination
              sx={{
                'color': 'var(--alternative-text-color)',
                'display': 'flex',
                'justifyContent': 'center',
                '& .MuiTablePagination-selectLabel': { marginBottom: 0 },
                '& .MuiTablePagination-displayedRows': { marginBottom: 0 },
              }}
              rowsPerPageOptions={[ 5, 10, 25 ]}
              component='div'
              count={hits.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ) : (
            <></>
          )}
        </Paper>
      </Root>
      <Modal
        isOpen={fileNameModalOpen}
        onRequestClose={handleFileNameClose}
        ariaHideApp={false}
      >
        <div className='bite-score-file-modal'>
          <div className='display-flex justify-between width-100'>
            <Typography variant='h5'>File Name:</Typography>
            <TicketButton
              onClick={handleFileNameClose}
              className='cursor-pointer'
            />
          </div>
          <div className='display-flex justify-end width-100'>
            <StyledInput
              value={fileName}
              onChange={handleFileNameChange}
              variant='outlined'
              className='margin-right-20px'
            />
            <NextButton
              variant='contained'
              color='primary'
              onClick={() => {
                handleCSV();
                handleFileNameClose();
              }}
            >
              Download
            </NextButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ResultsTable;
